/**
 * Typed calls for competition management and Present mode.
 * Permissions always come from the server (GET /competitions/:id/my_permissions);
 * this module never decides who may edit what.
 */
import { apiService } from './api';
import { Competition } from '../types/interfaces';
import { CompetitionRubric } from '../components/competition/RubricScoreForm';
import { MultiManifest } from '../components/viewers/submissionMedia';

export type CompetitionRole = 'platform_admin' | 'host_admin' | 'host_basic';

export const ROLE_LABELS: Record<CompetitionRole, string> = {
  platform_admin: 'Platform admin',
  host_admin: 'Host admin',
  host_basic: 'Host',
};

export interface CompetitionPermissions {
  role: CompetitionRole;
  editable_fields: string[];
  actions: string[];
  field_min_role: Record<string, CompetitionRole>;
}

export type ContentKind = 'readme' | 'evaluation';

export interface CompetitionContent {
  kind: ContentKind;
  markdown: string;
  public_url: string | null;
  exists: boolean;
}

export interface PresentEntry {
  entry_id: string;
  name: string;
  members: string[];
  has_submission: boolean;
  version: number | null;
  submitted_at: string | null;
}

export interface PresentCompetitionSummary {
  id: number;
  title: string;
  type?: string | null;
  competition_mode?: 'individual' | 'team';
  logo_path?: string | null;
}

export interface PresentList {
  competition: PresentCompetitionSummary;
  entries: PresentEntry[];
}

export interface PresentSubmission {
  submission_id: number;
  submission_version: number;
  file_type?: string | null;
  url: string;
  manifest?: MultiManifest | null;
  metadata_title?: string | null;
  metadata_track?: string | null;
  metadata_team?: string | null;
  team_name?: string | null;
}

export interface PresentEntryDetail extends PresentEntry {
  submission: PresentSubmission | null;
}

export interface RubricCriterionDefinition {
  criterion_key: string;
  label: string;
  description: string;
  guidance: Record<string, string>;
  weight: number;
}

export interface RubricDefinition {
  title: string;
  criteria: RubricCriterionDefinition[];
}

export interface RubricConfig {
  evaluation_mode: 'rubric' | 'single_score' | 'pairwise';
  rubric: CompetitionRubric | null;
  locked?: boolean;
}

export function errorStatus(err: unknown): number | undefined {
  const status = (err as { status?: unknown } | null)?.status;
  return typeof status === 'number' ? status : undefined;
}

/** Message from the api.js rejection shape ({ status, message }); 422 lists are joined. */
export function errorMessage(err: unknown, fallback: string): string {
  const message = (err as { message?: unknown } | null)?.message;
  if (typeof message === 'string' && message.trim()) return message;
  if (Array.isArray(message)) {
    const parts = message
      .map((item) => (item && typeof item === 'object' ? (item as { msg?: unknown }).msg : item))
      .filter((part): part is string => typeof part === 'string' && part.trim() !== '');
    if (parts.length) return parts.join('; ');
  }
  if (message && typeof message === 'object' && !Array.isArray(message)) {
    const inner = (message as { message?: unknown }).message;
    if (typeof inner === 'string' && inner.trim()) return inner;
  }
  return fallback;
}

export function isHostAdminOrAbove(role?: CompetitionRole | string | null): boolean {
  return role === 'platform_admin' || role === 'host_admin';
}

export async function fetchMyPermissions(competitionId: number): Promise<CompetitionPermissions> {
  const res = await apiService.get(`/competitions/${competitionId}/my_permissions`);
  return res.data;
}

export async function fetchContent(competitionId: number, kind: ContentKind): Promise<CompetitionContent> {
  const res = await apiService.get(`/competitions/${competitionId}/content/${kind}`);
  return res.data;
}

export async function saveContent(competitionId: number, kind: ContentKind, markdown: string): Promise<CompetitionContent> {
  const res = await apiService.put(`/competitions/${competitionId}/content/${kind}`, { markdown });
  return res.data;
}

export async function uploadLogo(pathId: string, file: File): Promise<{ public_url: string }> {
  const form = new FormData();
  form.append('path_id', pathId);
  form.append('asset_type', 'logo');
  // The server requires this exact filename.
  form.append('file', file, 'logo.png');
  const res = await apiService.upload('/utils/competitions/upload_asset', form);
  return res.data;
}

export async function fetchRubricConfig(competitionId: number): Promise<RubricConfig> {
  const res = await apiService.get(`/evaluations/rubric/${competitionId}`);
  return res.data;
}

export async function saveRubric(competitionId: number, definition: RubricDefinition): Promise<{ evaluation_mode: 'rubric'; rubric: CompetitionRubric }> {
  const res = await apiService.put(`/evaluations/rubric/${competitionId}`, definition);
  return res.data;
}

export async function fetchPresentList(competitionId: number): Promise<PresentList> {
  const res = await apiService.get(`/competitions/${competitionId}/present`);
  return res.data;
}

export async function fetchPresentEntry(competitionId: number, entryId: string): Promise<PresentEntryDetail> {
  const res = await apiService.get(`/competitions/${competitionId}/present/${encodeURIComponent(entryId)}`);
  return res.data;
}

/**
 * Save changed competition fields. Platform-admin-only fields go to /update;
 * everything else goes to /manage_update (which also resyncs judge pairs when
 * evaluation rounds change), even for platform admins.
 */
export async function saveCompetitionFields(
  competitionId: number,
  changes: Record<string, unknown>,
  fieldMinRole: Record<string, CompetitionRole>,
): Promise<Competition> {
  const core: Record<string, unknown> = {};
  const managed: Record<string, unknown> = {};
  Object.entries(changes).forEach(([field, value]) => {
    if (fieldMinRole[field] === 'platform_admin') core[field] = value;
    else managed[field] = value;
  });
  let updated: Competition | null = null;
  if (Object.keys(core).length) {
    updated = (await apiService.post(`/competitions/update/${competitionId}`, core)).data;
  }
  if (Object.keys(managed).length) {
    updated = (await apiService.post(`/competitions/manage_update/${competitionId}`, managed)).data;
  }
  if (!updated) throw new Error('No changes to save');
  return updated;
}

export const MAX_ALLOWANCE = 100000;

export interface JudgeAllowanceConflict {
  username: string;
  used: number;
}

/** Judges listed in a 409 "allowance below used" error, or [] for any other error. */
export function conflictJudges(err: unknown): JudgeAllowanceConflict[] {
  const message = (err as { message?: unknown } | null)?.message;
  const judges = message && typeof message === 'object' ? (message as { judges?: unknown }).judges : undefined;
  return Array.isArray(judges) ? (judges as JudgeAllowanceConflict[]) : [];
}

export async function setJudgeAllowance(
  competitionId: number,
  maxVotes: number,
  usernames?: string[],
): Promise<{ updated: number; max_votes: number }> {
  const body = usernames ? { max_votes: maxVotes, usernames } : { max_votes: maxVotes };
  const res = await apiService.post(`/competitions/${competitionId}/judges/allowance`, body);
  return res.data;
}

export interface JudgingCounts {
  matches: number;
  team_matches: number;
  manual_scores: number;
  rubric_evaluations: number;
  judges: number;
  pair_reservations: number;
  finalists: number;
}

export async function fetchResetPreview(competitionId: number): Promise<JudgingCounts> {
  const res = await apiService.get(`/competitions/${competitionId}/reset_judging/preview`);
  return res.data;
}

export async function resetJudging(competitionId: number, confirmTitle: string): Promise<JudgingCounts & { reset: boolean }> {
  const res = await apiService.post(`/competitions/${competitionId}/reset_judging`, { confirm_title: confirmTitle });
  return res.data;
}

export interface AdminSubmission {
  submission_id: number;
  version: number | null;
  submitted_at: string | null;
  file_type: string | null;
  owner_label: string | null;
  team_id: number | null;
  user_id: number | null;
  votes: number;
  manual_scores: number;
  rubric_evaluations: number;
}

/** Display name for a submission's owner; the server may send an empty or null label. */
export function ownerLabel(submission: Pick<AdminSubmission, 'submission_id' | 'owner_label'>): string {
  return submission.owner_label?.trim() || `Submission #${submission.submission_id}`;
}

export async function fetchAdminSubmissions(competitionId: number): Promise<AdminSubmission[]> {
  const res = await apiService.get(`/competitions/${competitionId}/submissions/admin`);
  return res.data;
}

export interface DeleteSubmissionResult {
  deleted: boolean;
  removed: { matches: number; manual_scores: number; rubric_evaluations: number; judges_refunded: number };
  files_deleted: number;
  files_failed: string[];
}

export async function deleteSubmission(competitionId: number, submissionId: number): Promise<DeleteSubmissionResult> {
  const res = await apiService.delete(`/competitions/${competitionId}/submissions/${submissionId}`);
  return res.data;
}

export interface CountdownSettings {
  enabled: boolean;
  ends_at: string | null;
  label: string | null;
  server_time: string;
}

export interface CountdownChanges {
  countdown_enabled?: boolean;
  countdown_ends_at?: string;
  countdown_label?: string;
  clear_countdown_ends_at?: boolean;
}

export async function fetchCountdown(competitionId: number): Promise<CountdownSettings> {
  const res = await apiService.get(`/competitions/${competitionId}/countdown`);
  return res.data;
}

export async function saveCountdown(competitionId: number, changes: CountdownChanges): Promise<void> {
  await apiService.post(`/competitions/manage_update/${competitionId}`, changes);
}
